import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { IBranchModel } from 'src/app/logic/branch/models/branch.model';
import { BranchesService } from 'src/app/services/branches.service';
import { OperatingHoursService } from 'src/app/services/operating-hours.service';
import {
  buildGroupedScheduleLinesFromOperatingHours,
  formatGroupedScheduleHours,
  formatGroupedScheduleLineLabel,
  GroupedScheduleLineView,
} from 'src/app/shared/promotion-detail-modal/promotion-display.util';

interface GroupedBranches {
  city: string;
  branches: IBranchModel[];
}

@Component({
  selector: 'app-branch-selector',
  templateUrl: './branch-selector.component.html',
  styleUrls: ['./branch-selector.component.scss'],
})
export class BranchSelectorComponent implements OnInit, OnChanges, OnDestroy {
  @Input() branch!: IBranchModel;
  @Input() branches: IBranchModel[] = [];
  @Output() branchSelected = new EventEmitter<IBranchModel>();

  public isMenuOpen = false;
  public groupedBranches: GroupedBranches[] = [];
  public allBranches: IBranchModel[] = [];
  public scheduleLines: GroupedScheduleLineView[] = [];

  private scheduleSubscription?: Subscription;
  private langChangeSubscription?: Subscription;
  private readonly scheduleCache = new Map<number, GroupedScheduleLineView[]>();

  constructor(
    private readonly branchesService: BranchesService,
    private readonly operatingHoursService: OperatingHoursService,
    private readonly translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.langChangeSubscription = this.translate.onLangChange.subscribe(() => {
      this.scheduleLines = [...this.scheduleLines];
    });
    this.loadBranches();
    if (this.branch?.id) {
      this.loadBranchSchedule(this.branch.id);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['branch'] && this.branch?.id) {
      this.loadBranchSchedule(this.branch.id);
    }
  }

  ngOnDestroy(): void {
    this.scheduleSubscription?.unsubscribe();
    this.langChangeSubscription?.unsubscribe();
  }

  loadBranches(): void {
    if (this.branches && this.branches.length > 0) {
      this.allBranches = this.branches;
      this.groupBranchesByCity();
    } else {
      this.branchesService.getAll().subscribe({
        next: (branches) => {
          this.allBranches = branches;
          this.groupBranchesByCity();
        },
        error: (error) => {
          console.error('Error loading branches:', error);
        },
      });
    }
  }

  groupBranchesByCity(): void {
    const grouped: { [key: string]: IBranchModel[] } = {};

    this.allBranches.forEach((branch) => {
      const city = branch.address?.locality || 'Sin ciudad';
      if (!grouped[city]) {
        grouped[city] = [];
      }
      grouped[city].push(branch);
    });

    this.groupedBranches = Object.keys(grouped)
      .sort((a, b) => a.localeCompare(b))
      .map((city) => ({
        city,
        branches: grouped[city],
      }));
  }

  toggleMenu(): void {
    this.isMenuOpen = !this.isMenuOpen;
  }

  selectBranch(selectedBranch: IBranchModel): void {
    this.branch = selectedBranch;
    this.loadBranchSchedule(selectedBranch.id);
    this.branchSelected.emit(selectedBranch);
    this.isMenuOpen = false;
  }

  isSelected(branch: IBranchModel): boolean {
    return this.branch?.id === branch.id;
  }

  getFullAddress(branch: IBranchModel): string {
    const address = branch.address;

    if (!address) {
      return '';
    }

    const parts = [
      address.street,
      address.sublocality,
      address.zipCode,
      address.locality,
      address.state,
    ].filter(Boolean);

    return parts.join(', ');
  }

  getScheduleLineLabel(line: GroupedScheduleLineView): string {
    return formatGroupedScheduleLineLabel(
      line,
      this.translate.instant.bind(this.translate)
    );
  }

  getScheduleHours(line: GroupedScheduleLineView): string {
    return formatGroupedScheduleHours(
      line,
      this.translate.instant.bind(this.translate)
    );
  }

  closeMenu(): void {
    this.isMenuOpen = false;
  }

  private loadBranchSchedule(branchId: number): void {
    const cached = this.scheduleCache.get(branchId);
    if (cached) {
      this.scheduleLines = cached;
      return;
    }

    this.scheduleSubscription?.unsubscribe();
    this.scheduleSubscription = this.operatingHoursService
      .getOperatingHours(branchId)
      .subscribe({
        next: (hours) => {
          const lines = buildGroupedScheduleLinesFromOperatingHours(hours);
          this.scheduleCache.set(branchId, lines);
          if (this.branch?.id === branchId) {
            this.scheduleLines = lines;
          }
        },
        error: (error) => {
          console.error('Error loading branch schedule:', error);
          if (this.branch?.id === branchId) {
            this.scheduleLines = [];
          }
        },
      });
  }
}
